import {
  HHButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { type Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { FormEvent, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validate as isValidUUID } from 'uuid';
import { routes } from '../../constants/strings/routes';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useGetSubscriptionsLazyQuery } from '../../types';
import LoadingComponent from '../utils/loading';

const UserSearch = ({
  titleColor,
  compactMode,
}: {
  titleColor?: string;
  compactMode?: boolean;
}): JSX.Element => {
  const navigation = useNavigate();
  const { setSnackbarState } = useSnackbarContext();
  const [userIdOrUuid, setUserIdOrUuid] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [isSearchingSubscription, setIsSearchingSubscription] = useState(false);
  const isUserEnabled =
    (userIdOrUuid && parseInt(userIdOrUuid, 10) > 0) ||
    isValidUUID(userIdOrUuid);
  const isSubscriptionDisabled =
    !subscriptionId || parseInt(subscriptionId, 10) === 0;

  const [getSubscriptions] = useGetSubscriptionsLazyQuery();

  const goToUserPage = (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
  ): void => {
    e.preventDefault();
    if (isUserEnabled) {
      navigation(
        `${routes.billing.home}/${routes.billing.userPath}/${userIdOrUuid}`,
      );
    }
  };

  const handleSubscriptionSearch = async (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (!isSubscriptionDisabled && !isSearchingSubscription) {
      setIsSearchingSubscription(true);
      try {
        const result = await getSubscriptions({
          variables: {
            ids: [parseInt(subscriptionId, 10)],
            includeVoid: true,
          },
        });

        if (result.data?.getSubscriptions?.items?.length) {
          const subscription = result.data.getSubscriptions.items[0];
          const extractedUserId = subscription.userId;
          navigation(
            `${routes.billing.home}/${routes.billing.userPath}/${extractedUserId}/subscriptions?includeVoid=true`,
          );
        } else {
          setSnackbarState('Subscription not found', 'error', 3000);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        setSnackbarState('Error fetching subscription data', 'error', 3000);
      } finally {
        setIsSearchingSubscription(false);
      }
    }
  };

  return (
    <HHStack
      border="1px solid lightgray"
      borderRadius={1}
      spacing={2}
      padding={4}
      sx={(theme): SystemStyleObject<Theme> => ({
        backgroundColor: theme.palette.background.default,
      })}
    >
      <HHTypography hhVariant="body" color={titleColor ?? ''}>
        User Journey
      </HHTypography>
      <form onSubmit={goToUserPage}>
        <HHStack direction="row" spacing={2}>
          <HHTextField
            hhVariant="variant-bypass"
            placeholder="Search by User ID or UUID"
            inputProps={{
              inputMode: 'text',
            }}
            value={userIdOrUuid}
            onChange={(e): void => setUserIdOrUuid(e.target.value)}
            type="text"
            InputLabelProps={{ sx: { color: 'gray' } }}
            sx={{ minWidth: 150 }}
            size="small"
            InputProps={{
              sx: { paddingLeft: 2 },
              startAdornment: (
                <SearchOutlinedIcon
                  sx={{
                    color: 'action.active',
                  }}
                />
              ),
            }}
            fullWidth
          />
          {!compactMode && (
            <HHButton
              hhVariant="contained"
              type="submit"
              disabled={!isUserEnabled}
              onClick={goToUserPage}
            >
              Search
            </HHButton>
          )}
        </HHStack>
      </form>
      <form onSubmit={handleSubscriptionSearch}>
        <HHStack direction="row" spacing={2}>
          <HHTextField
            hhVariant="variant-bypass"
            placeholder="Search by Subscription ID"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              min: '0',
            }}
            value={subscriptionId}
            onChange={(e): void => setSubscriptionId(e.target.value)}
            type="number"
            InputLabelProps={{ sx: { color: 'gray' } }}
            sx={{ minWidth: 150 }}
            InputProps={{
              sx: { paddingLeft: 2 },
              startAdornment: isSearchingSubscription ? (
                <LoadingComponent size={20} />
              ) : (
                <SearchOutlinedIcon sx={{ color: 'action.active' }} />
              ),
            }}
            fullWidth
            disabled={isSearchingSubscription}
          />
          {!compactMode && (
            <HHButton
              hhVariant="contained"
              type="submit"
              disabled={isSubscriptionDisabled || isSearchingSubscription}
              onClick={handleSubscriptionSearch}
            >
              {isSearchingSubscription ? (
                <LoadingComponent size={20} />
              ) : (
                'Search'
              )}
            </HHButton>
          )}
        </HHStack>
      </form>
    </HHStack>
  );
};

export default UserSearch;
